import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, authErrorMessage } from '../../lib/api';
import type { PaymentAttempt } from '../../types/payments';
import { AttemptHistory } from '../../components/payments/AttemptHistory';

function extractAttempts(payload: unknown): PaymentAttempt[] {
  if (Array.isArray(payload)) return payload as PaymentAttempt[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as { attempts?: unknown }).attempts)) {
    return (payload as { attempts: PaymentAttempt[] }).attempts;
  }
  return [];
}

/** Audit history view (CHO-754): every attempt, answering "did we already pay that?". */
export default function PaymentHistoryPage() {
  const [attempts, setAttempts] = useState<PaymentAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getPaymentAttempts();
      setAttempts(extractAttempts(data));
    } catch (err) {
      // CHO-759: an auth-class failure (401 session expired, 429 sign-in
      // lockout) must never masquerade as an empty history
      // ("No payment attempts yet.") or an unreachable service.
      setError(
        authErrorMessage(err) ??
          (err instanceof Error
            ? `The invoice payment service is unreachable (${err.message})`
            : 'The invoice payment service is unreachable'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment attempt history</h1>
          <p className="text-sm text-gray-500 mt-1">
            The record of every payment attempt.{' '}
            <Link to="/payments" className="text-gray-700 underline underline-offset-2 hover:text-gray-900">
              Back to the queue
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p role="alert" className="mb-4 text-sm font-medium text-red-800 bg-red-50 border border-red-200 rounded-md px-4 py-3">
          {error}
        </p>
      )}

      {!error && <AttemptHistory attempts={attempts} />}
    </div>
  );
}
